import React, { useCallback, useMemo } from 'react'
import { compose } from 'redux'
import { Grid, Typography, withStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import Title from '../../layout/Title'
import { BLOCK_ACTION_TYPE, SVB_TASKS_CONTENT_FORM_FIELDS, SVB_TASKS_CONTENT_FORM_NAME, SVB_TASKS_TABLE_NAME } from '../tool/suiviParBloc.constants'
import { injectCreateTask } from '../../../../gestionTache/injectors/createTaskInjector'
import { injectReminderForm } from '../../../service/reminderFormInjector'
import { injectActionsForm } from '../../../service/actionsFormInjector'
import { contentStyles } from '../tool/suiviParBloc.styles'
import Checkbox from '../../../../../../components/form/Checkbox'
import Button from '../../../../../../components/Button'
import ButtonWithMenu from '../../../../../../components/ButtonWithMenu'
import SpringDataTableWithLoader from '../../../../../../components/layout/SpringDataTableWithLoader'
import NoResultPage from '../../../../../../components/NoResultPage'
import { connect } from 'react-redux'
import { dataTableActions } from 'isotope-client'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import { useTourPlaineContext } from '../../../TourPlaineContext'
import { FormattedMessage, useIntl } from 'react-intl'
import AddCommentIccon from '@material-ui/icons/AddComment'
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import { ACTIONS_KEY, TASK_DATATABLE_KEYS, TOUR_PLAINE_TABS } from '../../../tool/tourPlaine.constants'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'
import { TASKS_URI } from '../service/suiviParBlocApi'
import { injectFamilleTacheValueList, injectTypeTacheValueList } from '../../../../../common/valueLists/valueListInjectors'
import { capitalize } from '../../../../../../utils/utils'
import IconButton from '../../../../../../components/IconButton'
import EditIcon from '@material-ui/icons/Edit'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import OutlinedFlagIcon from '@material-ui/icons/OutlinedFlag'
import { taskToApp } from '../../../tool/tourPlaine.mapper'
import { openTacheDetail } from '../../../../gestionTache/injectors/openTacheDetailsInjector'
import { injectTaskUpdateForm } from '../../../service/taskUpdateFormInjector'
import { LocalDate } from '../../../../../../utils/date/local-date'
import { LocalizedDateFormat } from '../../../../../../utils/date/model/date'
import TacheStatutIcon from '../../../../gestionTache/component/common/TacheStatutIcon'
import { ETAT_TACHE_UPPERCASE } from '../../../../../../utils/constants'

const SORTABLE_KEYS = [
	TASK_DATATABLE_KEYS.TASK,
	TASK_DATATABLE_KEYS.CROP,
	TASK_DATATABLE_KEYS.BOARD,
	TASK_DATATABLE_KEYS.PLANNED_DATE,
	TASK_DATATABLE_KEYS.INITIAL_DATE
]

/**
 * TasksContent for SuiviParBloc
 * @returns {JSX.Element}
 */
const TasksContent = ({
	classes,
	block,
	openReminderForm,
	getActions,
	openCreateTask,
	taskHarvest,
	taskWaiting,
	typeTacheList,
	familleTacheList,
	openTaskUpdateForm,
	selectedRows,
	refreshTable,
	resetSelectedRows
}) => {
	const { formatMessage } = useIntl()
	const { currentWeek, currentCampagneInProgress } = useTourPlaineContext()

	const filters = useMemo(() => ({
		idBloc: block.uniqueId,
		dateDebut: currentWeek,
		onlyRecolte: taskHarvest,
		onlyTachesEnAttenteArbitrage: taskWaiting
	}), [block.uniqueId, currentWeek, taskHarvest, taskWaiting])

	// Les actions du tour de plaine travaillent sur les tâches mappées
	const selectedTasks = useMemo(() => taskToApp(selectedRows), [selectedRows])

	const refreshData = useCallback((shouldUnselect) => {
		if (shouldUnselect) {
			resetSelectedRows()
		}
		refreshTable()
	}, [resetSelectedRows, refreshTable])

	const headers = useMemo(() => {
		const result = [
			{
				key: TASK_DATATABLE_KEYS.FAMILLE,
				name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.tasks.table.headers.${TASK_DATATABLE_KEYS.FAMILLE}`} /></Typography>,
				tableHeaderColumnProps: {
					width: 100
				},
				sortable: SORTABLE_KEYS.includes(TASK_DATATABLE_KEYS.FAMILLE),
				checked: true,
				render: row => {
					const family = familleTacheList.find(famille => famille.code === row.famille)
					return (<Typography className={classes.dataTableBoldText}>
						{family?.label}
					</Typography>)
				}
			},
			{
				key: TASK_DATATABLE_KEYS.TASK,
				name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.tasks.table.headers.${TASK_DATATABLE_KEYS.TASK}`} /></Typography>,
				tableHeaderColumnProps: {
					width: 145
				},
				sortable: SORTABLE_KEYS.includes(TASK_DATATABLE_KEYS.TASK),
				render: row => {
					const task = typeTacheList.find(typeTache => typeTache.code === row.tache)
					return (<Typography className={classes.dataTableBoldText}>
						{task.label}
					</Typography>)
				}
			},
			{
				key: TASK_DATATABLE_KEYS.CROP,
				name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.tasks.table.headers.${TASK_DATATABLE_KEYS.CROP}`} /></Typography>,
				tableHeaderColumnProps: {
					width: 145
				},
				sortable: SORTABLE_KEYS.includes(TASK_DATATABLE_KEYS.CROP),
				render: row => (
					<Typography className={classes.dataTableNormalText}>
						{row.assolement?.culture?.nom}
					</Typography>
				)
			},
			{
				key: TASK_DATATABLE_KEYS.BOARD,
				name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.tasks.table.headers.${TASK_DATATABLE_KEYS.BOARD}`} /></Typography>,
				tableHeaderColumnProps: {
					width: 80
				},
				sortable: SORTABLE_KEYS.includes(TASK_DATATABLE_KEYS.BOARD),
				render: row => (
					<Typography className={classes.dataTableNormalText}>
						{row.planche?.nom ?? '-'}
					</Typography>
				)
			},
			{
				key: TASK_DATATABLE_KEYS.PLANNED_DATE,
				name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.tasks.table.headers.${TASK_DATATABLE_KEYS.PLANNED_DATE}`} /></Typography>,
				tableHeaderColumnProps: {
					width: 100
				},
				sortable: SORTABLE_KEYS.includes(TASK_DATATABLE_KEYS.PLANNED_DATE),
				render: row => (
					<div className={classes.cell}>
						<Typography className={classes.dataTableNormalText}>
							{capitalize(LocalDate.fromAPI(row.datePrevue).format(LocalizedDateFormat.LONG_DATE_WITHOUT_YEAR_STRING))}
						</Typography>
					</div>
				)
			},
			{
				key: TASK_DATATABLE_KEYS.MATERIAL,
				name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.tasks.table.headers.${TASK_DATATABLE_KEYS.MATERIAL}`} /></Typography>,
				tableHeaderColumnProps: {
					width: 145
				},
				sortable: SORTABLE_KEYS.includes(TASK_DATATABLE_KEYS.MATERIAL),
				render: row => {
					const materials = row.materiels.join(', ')
					return (
						<div className={classes.dataTableCell}>
							<Typography className={classes.dataTableNormalText}>
								{materials || '-'}
							</Typography>
						</div>
					)
				}
			},
			{
				key: TASK_DATATABLE_KEYS.ARBITRAGE,
				name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.tasks.table.headers.${TASK_DATATABLE_KEYS.ARBITRAGE}`} /></Typography>,
				tableHeaderColumnProps: {
					width: 145
				},
				sortable: SORTABLE_KEYS.includes(TASK_DATATABLE_KEYS.ARBITRAGE),
				render: row => {
					return (
						<div>
							{row.statut === ETAT_TACHE_UPPERCASE.TERMINEE ? (
									<Typography className={classes.dataTableNormalText}>
										<TacheStatutIcon statut={row.statut} isEnRetard={row.enRetard} />
									</Typography>)
								:
								<div>
									{row.arbitrageRecolte && (
										<div className={classes.cell}>
											<CheckCircleOutlineIcon />
											<Typography className={classes.dataTableNormalText}>
												<FormattedMessage
													id="tourPlaine.suiviParBloc.tasks.table.cells.taskHarvest"
												/>
											</Typography>
										</div>
									)}
									{row.arbitrageTacheGenerique && (
										<div className={classes.cell}>
											<OutlinedFlagIcon />
											<Typography className={classes.dataTableNormalText}>
												<FormattedMessage
													id="tourPlaine.suiviParBloc.tasks.table.cells.taskGeneric"
												/>
											</Typography>
										</div>
									)}
									{row.arbitrageRetard && (
										<div className={classes.cell}>
											<WarningRoundedIcon />
											<Typography className={classes.dataTableNormalText}>
												<FormattedMessage
													id="tourPlaine.suiviParBloc.tasks.table.cells.taskWaiting"
												/>
											</Typography>
										</div>
									)}
								</div>
							}
						</div>
					)
				}
			},
			{
				key: TASK_DATATABLE_KEYS.INITIAL_DATE,
				name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviParBloc.tasks.table.headers.${TASK_DATATABLE_KEYS.INITIAL_DATE}`} /></Typography>,
				tableHeaderColumnProps: {
					width: 100
				},
				sortable: SORTABLE_KEYS.includes(TASK_DATATABLE_KEYS.INITIAL_DATE),
				render: row => {
					const plannedDate = LocalDate.fromAPI(row.datePrevue)
					const initialDate = LocalDate.fromAPI(row.dateInitiale)
					const nbWeeksLate = Math.floor(plannedDate._diff(initialDate, 'week'))
					const isLate = nbWeeksLate > 0

					return (
						<Typography className={classes.dataTableInitialDateText}>
							{capitalize(LocalDate.fromAPI(row.dateInitiale).format(LocalizedDateFormat.LONG_DATE_WITHOUT_YEAR_STRING))}
							{isLate && <><br /><FormattedMessage id="tourPlaine.suiviParBloc.tasks.table.cells.lateWeek" values={{ nbWeek: nbWeeksLate }} /></>}
						</Typography>
					)
				}
			}]

		if (currentCampagneInProgress) {
			result.push({
				key: 'action',
				tableHeaderColumnProps: {
					align: 'center',
					width: 24
				},
				render: row => (
					<IconButton onClick={(event) => {
						openTaskUpdateForm({ ...taskToApp([row])[0], plannedDate: row.dateDebutPlanification }, refreshData)
						return event.stopPropagation()
					}}>
						<EditIcon color="primary" />
					</IconButton>
				)
			})
		}

		return result
	}, [classes, typeTacheList, familleTacheList, currentCampagneInProgress, openTaskUpdateForm, refreshData])

	const actions = useMemo(() => getActions([
				ACTIONS_KEY.MOVE_TASK,
				ACTIONS_KEY.CONFIRM_HARVEST,
				ACTIONS_KEY.SPECIFY_GENERIC_TASK,
				ACTIONS_KEY.ADD_INSTRUCTION,
				ACTIONS_KEY.DELETE_TASK
			],
			selectedTasks,
			TOUR_PLAINE_TABS.SUIVI_BLOC,
			refreshData),
		[selectedTasks, getActions, refreshData])

	return (<div className={classes.root}>
		<Grid container className={classes.header}>
			<Title actionBlock={BLOCK_ACTION_TYPE.TASK} />
			<Grid item container direction="row" justify="flex-start" alignItems="center">
				<Grid item container xs direction="row" alignItems="center">
					<Grid item style={{ marginRight: 24 }}>
						<Typography className={classes.filterText}>
							<FormattedMessage id="tourPlaine.suiviParBloc.tasks.filter" />
						</Typography>
					</Grid>
					<Field
						name={SVB_TASKS_CONTENT_FORM_FIELDS.TASK_HARVEST}
						component={Checkbox}
						label={<Typography className={classes.filterText} style={{ marginLeft: 15 }}>
							<FormattedMessage id="tourPlaine.suiviParBloc.tasks.harvest" />
						</Typography>
						}
					/>
					<Field
						name={SVB_TASKS_CONTENT_FORM_FIELDS.TASK_WAITING}
						component={Checkbox}
						label={<Typography className={classes.filterText} style={{ marginLeft: 10 }}>
							<FormattedMessage id="tourPlaine.suiviParBloc.tasks.waiting" />
						</Typography>
						}
					/>
				</Grid>
				{currentCampagneInProgress && <Grid item container xs direction="row" justify="flex-end" alignItems="flex-end" className={classes.actionContainer}>
					<Button
						type="secondary"
						startIcon={<AddCommentIccon />}
						onClick={() => openReminderForm(`${formatMessage({ id: 'tourPlaine.tabBar.suiviBlock' })} / ${formatMessage({ id: 'tourPlaine.suiviParBloc.actionTab.tasks' })}`)}
						className={classes.button}
						style={{ minWidth: 145 }}
					>
						<FormattedMessage id="tourPlaine.actions.addReminder" />
					</Button>
					<Button
						type="secondary"
						startIcon={<PlaylistAddIcon />}
						onClick={() => openCreateTask(refreshData)}
						className={classes.button}
						style={{ minWidth: 150 }}
					>
						<FormattedMessage id="tourPlaine.actions.addTask" />
					</Button>
					<ButtonWithMenu
						type="primary"
						startIcon={<MoreHorizIcon />}
						className={classes.button}
						style={{ minWidth: 155, width: 155 }}
						items={actions}
						disabled={!selectedRows.length}
					>
						<FormattedMessage id="tourPlaine.actions.others" />
					</ButtonWithMenu>
				</Grid>}
			</Grid>
		</Grid>
		<Grid item container className={classes.dataTableContainer}>
			<SpringDataTableWithLoader
				nom={SVB_TASKS_TABLE_NAME}
				apiUrl={TASKS_URI}
				filters={filters}
				pageSizes={[25, 50, 100]}
				defaultPageSize={25}
				tableProps={{
					stickyHeader: true,
					className: classes.dataTable
				}}
				noResultFragment={<NoResultPage emptyStateMessageId="tourPlaine.suiviParBloc.tasks.table.empty" />}
				selectable={currentCampagneInProgress}
				displaySelectAll={currentCampagneInProgress}
				headers={headers}
				loaderStyle={{ width: '100%' }}
				messageStyle={{ verticalAlign: 'top', paddingTop: 72 }}
				hideContentOnLoad={false}
			/>
		</Grid>
	</div>)
}

TasksContent.propTypes = {
	classes: PropTypes.object,
	block: PropTypes.object,
	openCreateTask: PropTypes.func,
	openReminderForm: PropTypes.func,
	getActions: PropTypes.func,
	taskHarvest: PropTypes.bool,
	taskWaiting: PropTypes.bool,
	typeTacheList: PropTypes.array,
	familleTacheList: PropTypes.array,
	openTaskUpdateForm: PropTypes.func,
	selectedRows: PropTypes.array,
	refreshTable: PropTypes.func,
	resetSelectedRows: PropTypes.func
}
const mapStateToProps = (state) => {
	const selector = formValueSelector(SVB_TASKS_CONTENT_FORM_NAME)
	return ({
		taskHarvest: selector(state, SVB_TASKS_CONTENT_FORM_FIELDS.TASK_HARVEST),
		taskWaiting: selector(state, SVB_TASKS_CONTENT_FORM_FIELDS.TASK_WAITING),
		selectedRows: (state.dataTable[SVB_TASKS_TABLE_NAME] || {}).selectedRows || []
	})
}

const tableActions = {
	refreshTable: () => dataTableActions.refresh(SVB_TASKS_TABLE_NAME),
	resetSelectedRows: () => dataTableActions.selectRows(SVB_TASKS_TABLE_NAME, [])
}

export default compose(
	injectTaskUpdateForm,
	openTacheDetail,
	injectCreateTask,
	injectReminderForm,
	injectActionsForm,
	injectTypeTacheValueList,
	injectFamilleTacheValueList,
	connect(mapStateToProps, tableActions),
	reduxForm({
		form: SVB_TASKS_CONTENT_FORM_NAME,
		enableReinitialize: true
	}),
	withStyles(contentStyles)
)(TasksContent)
